import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { SocialPlatformFilter } from "./SocialPlatformSelector";
import { SocialCustomDates, SocialDateRange } from "./SocialToolbar";
import { Inbox, LayoutDashboard, MapPin, MoreHorizontal, SlidersHorizontal, Tags } from "lucide-react";
import { MobileBottomNav, MobileFab, MobileSheet, SheetOption } from "./mobile/MobileChrome";
import { useViewHistory } from "./mobile/navHistory";
import {
  PRESET_LABELS,
  type RangePreset,
  readStoredRange,
  rangeToQuery,
  resolveRange,
  storeRange,
  todaySantiago,
  type SocialRange,
} from "./socialRange";
import { usePageHeaderSlots } from "../../context/PageHeaderSlotContext";
import { fetchSocialOverview } from "../../api";
import type { SocialDetailPayload, SocialOverviewPayload, GertrudisAuditPayload } from "../../types";
import { SandboxResumen, type SandboxTheme } from "./sandbox/SandboxResumen";
import { SandboxBandeja } from "./sandbox/SandboxBandeja";
import { SandboxLocales } from "./sandbox/SandboxLocales";
import { SandboxTemas } from "./sandbox/SandboxTemas";
import { SocialMensajesTab } from "./mensajes/SocialMensajesTab";
import { SocialYoutubeComentarios } from "./youtube/SocialYoutubeComentarios";
import { useDocumentTheme } from "./useDocumentTheme";
import { useDashboard } from "../../context/DashboardContext";
import { byOrigin, channelOf, countByOrigin, localKey, truthy, type BandejaFocus, type Origen } from "./sandbox/resumenData";
import { SocialOrigenSelector } from "./SocialOrigenSelector";
import { SocialOrigenSelectorMobile } from "./mobile/SocialOrigenSelectorMobile";
import "./sandbox/sandbox.css";
import "./mobile/mobile.css";

const DESKTOP_QUERY = "(min-width: 768px)";
const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

function fmtIso(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  const month = MONTH_NAMES[Number(m) - 1];
  if (!y || !month || !d) return iso;
  return `${Number(d)} ${month} ${y}`;
}

function periodLabel(from: string | null | undefined, to: string | null | undefined, days: number): string {
  if (from && to) return `${fmtIso(from)} a ${fmtIso(to)}`;
  if (from) return `Desde ${fmtIso(from)}`;
  if (to) return `Hasta ${fmtIso(to)}`;
  return `Últimos ${days} días`;
}

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Días del rango, contando ambos extremos. */
function daysInclusive(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

interface Props {
  data: SocialDetailPayload;
  clientSlug: string;
  onAuditGenerated?: (audit: GertrudisAuditPayload) => void;
  range?: SocialRange;
  onRangeChange?: (range: SocialRange) => void;
  portalControls?: boolean;
  showHeader?: boolean;
}

/**
 * SocialTab (Producción)
 *
 * Aloja las 2 páginas del Reputation Desk:
 * 1. Resumen Ejecutivo (Main.dc.html)
 * 2. Bandeja de Respuesta HITL (Bandeja.dc.html)
 */
export function SocialTab({
  data,
  clientSlug,
  onAuditGenerated: _onAuditGenerated,
  range: propRange,
  onRangeChange,
  portalControls = true,
  showHeader,
}: Props) {
  const [platform, setPlatform] = useState<SocialPlatformFilter>("all");
  // Un solo origen rige todas las pestañas.
  const [origen, setOrigen] = useState<Origen>("all");
  const [overview, setOverview] = useState<SocialOverviewPayload | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localRange, setLocalRange] = useState<SocialRange>(readStoredRange);
  type Vista = "resumen" | "bandeja" | "locales" | "temas" | "mensajes" | "youtube";
  const [activeView, setActiveView] = useState<Vista>("resumen");
  const [bandejaFocus, setBandejaFocus] = useState<BandejaFocus | null>(null);
  // Local que la vista Locales abre de entrada en su detalle. null muestra el comparativo.
  const [localesFocusId, setLocalesFocusId] = useState<string | null>(null);
  // Tema que la vista Temas abre de entrada. null deja el primero de la lista.
  const [temasFocusKey, setTemasFocusKey] = useState<string | null>(null);
  const exportRef = useRef<(() => void) | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { session } = useDashboard();
  const showMensajes = !(session?.isClientUser || session?.isClientPortal);
  const range = propRange ?? localRange;
  const theme = useDocumentTheme();
  const headerSlots = usePageHeaderSlots();
  const isDesktop = useIsDesktop();
  const goView = useCallback((v: string) => setActiveView(v as Vista), []);
  useViewHistory(activeView, goView, !isDesktop);

  const changeRange = (next: SocialRange) => {
    if (onRangeChange) {
      onRangeChange(next);
    } else {
      setLocalRange(next);
      storeRange(next);
    }
  };

  const query = useMemo(() => rangeToQuery(range), [range]);
  const queryKey = `${query.desde ?? ""}|${query.hasta ?? ""}`;
  const { desde: tableFrom, hasta: tableTo } = useMemo(() => resolveRange(range), [range]);
  const customIncomplete = range.preset === "custom" && !range.desde && !range.hasta;

  const recentComments = data.recentComments ?? [];
  const googleLocales = useMemo(() => data.locales ?? [], [data.locales]);

  // La vista YouTube es de la agencia: los portales de cliente no la ven.
  const youtubeComments = useMemo(
    () => (showMensajes ? recentComments.filter((c) => channelOf(c) === "youtube") : []),
    [recentComments, showMensajes],
  );
  const hasYoutube = youtubeComments.length > 0;

  // La vista Locales solo tiene sentido con 2 o más fichas de Google
  const hasLocales = useMemo(() => {
    const ids = new Set<string>(googleLocales.map((l) => l.id));
    for (const c of recentComments) {
      if (channelOf(c) !== "gbp") continue;
      const k = localKey(c);
      if (k) ids.add(k);
    }
    return ids.size >= 2;
  }, [recentComments, googleLocales]);

  useEffect(() => {
    if (activeView === "locales" && !hasLocales) setActiveView("resumen");
    if (activeView === "youtube" && !hasYoutube) setActiveView("resumen");
  }, [activeView, hasLocales, hasYoutube]);

  const availablePlatforms = useMemo<SocialPlatformFilter[]>(() => {
    const found = new Set<SocialPlatformFilter>();
    const checkPlat = (raw: unknown) => {
      const p = String(raw || "").toLowerCase();
      if (p === "meta_ig" || p.includes("ig") || p.includes("instagram")) found.add("meta_ig");
      if (p === "meta_fb" || p.includes("fb") || p.includes("facebook")) found.add("meta_fb");
      if (p === "gbp" || p.includes("google")) found.add("gbp");
    };

    if (data.summary && data.summary.length > 0) {
      for (const row of data.summary) {
        const count = Number(row.total_comments || 0);
        if (count > 0 || row.total_comments === undefined) {
          checkPlat(row.platform);
        }
      }
    }
    if (data.recentComments && data.recentComments.length > 0) {
      for (const row of data.recentComments) {
        checkPlat(row.platform);
      }
    }
    return Array.from(found);
  }, [data.summary, data.recentComments]);

  useEffect(() => {
    if (availablePlatforms.length === 1 && platform !== availablePlatforms[0]) {
      setPlatform(availablePlatforms[0]);
    } else if (availablePlatforms.length > 1 && platform !== "all" && !availablePlatforms.includes(platform)) {
      setPlatform("all");
    }
  }, [availablePlatforms, platform]);

  useEffect(() => {
    let active = true;
    setLoadingOverview(true);
    setError(null);

    fetchSocialOverview(clientSlug, platform, query, origen)
      .then((res) => {
        if (active) setOverview(res);
      })
      .catch((err) => {
        if (active) {
          console.error("Error al cargar datos del tab Social:", err);
          setError(err instanceof Error ? err.message : "Error cargando métricas de Social.");
        }
      })
      .finally(() => {
        if (active) setLoadingOverview(false);
      });

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug, platform, queryKey, origen]);

  // Mantiene el indicador de carga visible al menos medio segundo tras cambiar fechas o canal
  const [switching, setSwitching] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSwitching(true);
    const t = setTimeout(() => setSwitching(false), 600);
    return () => clearTimeout(t);
  }, [queryKey, platform, origen]);

  // Comentarios del rango activo, sin respuestas propias del cliente y sin separar por origen: alimentan los conteos del selector.
  const rangeCommentsTodos = useMemo(() => {
    if (!recentComments.length) return [];
    return recentComments.filter((c) => {
      if (c.is_owner_author || c.is_reply) return false;
      const d = String(c.date || "").slice(0, 10);
      if (!d) return false;
      if (tableFrom && d < tableFrom) return false;
      if (tableTo && d > tableTo) return false;
      return true;
    });
  }, [recentComments, tableFrom, tableTo]);

  const origenCounts = useMemo(() => countByOrigin(rangeCommentsTodos), [rangeCommentsTodos]);
  const rangeComments = useMemo(() => byOrigin(rangeCommentsTodos, origen), [rangeCommentsTodos, origen]);
  const scopedRecent = useMemo(() => byOrigin(recentComments, origen), [recentComments, origen]);

  // Críticos del rango: la marca la define el servidor, igual que en el Resumen y la Bandeja.
  const urgentCount = useMemo(() => rangeComments.filter((c) => truthy(c.critico)).length, [rangeComments]);

  // Período anterior de igual largo, justo antes del rango activo.
  const prevWindow = useMemo(() => {
    if (!tableFrom) return null;
    const to = tableTo ?? todaySantiago();
    const len = daysInclusive(tableFrom, to);
    return { from: shiftIso(tableFrom, -len), to: shiftIso(tableFrom, -1), days: len };
  }, [tableFrom, tableTo]);

  const prevComments = useMemo(() => {
    if (!prevWindow) return null;
    return scopedRecent.filter((c) => {
      if (c.is_owner_author || c.is_reply) return false;
      const d = String(c.date || "").slice(0, 10);
      return Boolean(d) && d >= prevWindow.from && d <= prevWindow.to;
    });
  }, [scopedRecent, prevWindow]);

  const effectiveShowHeader = showHeader !== undefined ? showHeader : !portalControls;

  // Texto que reemplaza a la vista cuando el origen elegido no deja nada que mostrar. Mensajes no se separa por origen.
  const sinComentarios =
    origen !== "all" && rangeComments.length === 0
      ? `No hay comentarios de ${origen === "paid" ? "anuncios" : "orgánicos"} en este período.`
      : null;
  const origenAviso =
    activeView === "mensajes" && showMensajes
      ? null
      : origen === "paid" && activeView === "youtube" && hasYoutube
        ? "YouTube solo tiene comentarios orgánicos."
        : origen === "paid" && activeView === "locales" && hasLocales
          ? "Las fichas de Google solo reciben reseñas orgánicas."
          : activeView === "youtube" && hasYoutube
            ? null
            : sinComentarios;

  const navPills = (
    <nav className="sbx-nav" aria-label="Secciones" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "resumen" ? "active" : ""}`}
        onClick={() => setActiveView("resumen")}
      >
        Resumen
      </button>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "bandeja" ? "active" : ""}`}
        onClick={() => openBandeja()}
      >
        Bandeja {urgentCount > 0 && <span className="sbx-nav-badge">{urgentCount}</span>}
      </button>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "temas" ? "active" : ""}`}
        onClick={() => openTemas()}
      >
        Temas
      </button>
      {hasLocales && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "locales" ? "active" : ""}`}
          onClick={() => openLocales()}
        >
          Locales
        </button>
      )}
      {showMensajes && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "mensajes" ? "active" : ""}`}
          onClick={() => setActiveView("mensajes")}
        >
          Mensajes
        </button>
      )}
      {hasYoutube && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "youtube" ? "active" : ""}`}
          onClick={() => setActiveView("youtube")}
        >
          YouTube
        </button>
      )}
    </nav>
  );

  const exportPdf = () => {
    if (exportRef.current) {
      exportRef.current();
      return;
    }
    setActiveView("resumen");
    setTimeout(() => exportRef.current?.(), 250);
  };

  const goToSection = (id: string) => {
    setMoreOpen(false);
    setActiveView("resumen");
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  // Abre la Bandeja con un local, tema o reseña ya elegidos. Sin foco, abre la Bandeja completa.
  const openBandeja = (focus?: BandejaFocus) => {
    setBandejaFocus(focus ?? null);
    setActiveView("bandeja");
    window.scrollTo({ top: 0 });
  };

  const openComment = (feedbackId: string) => openBandeja({ feedbackId, group: "meta", todos: true });

  const openLocales = (localId?: string) => {
    setMoreOpen(false);
    setLocalesFocusId(localId ?? null);
    setActiveView("locales");
    window.scrollTo({ top: 0 });
  };

  const openTemas = (topic?: string) => {
    setMoreOpen(false);
    setTemasFocusKey(topic ?? null);
    setActiveView("temas");
    window.scrollTo({ top: 0 });
  };

  const exportPdfBtn = (
    <button
      type="button"
      className="sbx-btn-pdf"
      onClick={exportPdf}
    >
      Exportar PDF
    </button>
  );

  const dateControl = (
    <div className="social-topbar-controls">
      <SocialDateRange range={range} onChange={changeRange} />
    </div>
  );

  return (
    <>
      {portalControls && isDesktop && headerSlots.left && createPortal(navPills, headerSlots.left)}
      {portalControls && isDesktop && headerSlots.actions && createPortal(exportPdfBtn, headerSlots.actions)}
      {portalControls && isDesktop && headerSlots.right && createPortal(dateControl, headerSlots.right)}

      {(!portalControls || !isDesktop || !headerSlots.right) && !propRange && (
        <div className="social-inline-toolbar" style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
          {dateControl}
          {exportPdfBtn}
        </div>
      )}

      {!isDesktop && (
        <>
          {activeView === "resumen" && (
            <MobileFab
              theme={theme}
              label="Filtros"
              badge={range.preset === "MTD" ? "Este mes" : range.preset === "YTD" ? "Este año" : PRESET_LABELS[range.preset]}
              icon={<SlidersHorizontal size={20} aria-hidden="true" />}
              onClick={() => setFiltersOpen(true)}
            />
          )}
          <MobileBottomNav
            theme={theme}
            active={activeView === "mensajes" || activeView === "youtube" ? "mas" : activeView}
            onSelect={(id) => {
              if (id === "mas") {
                setMoreOpen(true);
                return;
              }
              if (id === "temas") return openTemas();
              if (id === "locales") return openLocales();
              if (id === "bandeja") setBandejaFocus(null);
              setActiveView(id as Vista);
              window.scrollTo({ top: 0 });
            }}
            items={[
              { id: "resumen", label: "Resumen", icon: <LayoutDashboard size={20} aria-hidden="true" /> },
              { id: "bandeja", label: "Bandeja", icon: <Inbox size={20} aria-hidden="true" />, badge: urgentCount },
              { id: "temas", label: "Temas", icon: <Tags size={20} aria-hidden="true" /> },
              ...(hasLocales ? [{ id: "locales", label: "Locales", icon: <MapPin size={20} aria-hidden="true" /> }] : []),
              { id: "mas", label: "Más", icon: <MoreHorizontal size={20} aria-hidden="true" /> },
            ]}
          />
          <MobileSheet
            open={filtersOpen}
            title="Filtros del Resumen"
            theme={theme}
            onClose={() => setFiltersOpen(false)}
            footer={
              <>
                <button type="button" className="sbx-m-action is-quiet" onClick={exportPdf}>
                  Exportar informe PDF
                </button>
                <button type="button" className="sbx-m-action" onClick={() => setFiltersOpen(false)}>
                  Listo
                </button>
              </>
            }
          >
            <div className="sbx-m-group">Período</div>
            <div role="radiogroup" aria-label="Período" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(Object.keys(PRESET_LABELS) as RangePreset[]).map((key) => (
                <SheetOption
                  key={key}
                  label={PRESET_LABELS[key]}
                  selected={range.preset === key}
                  onClick={() => changeRange(key === "custom" ? { preset: key, desde: range.desde, hasta: range.hasta } : { preset: key })}
                />
              ))}
            </div>
            {range.preset === "custom" && <SocialCustomDates range={range} onChange={changeRange} />}
          </MobileSheet>
          <MobileSheet open={moreOpen} title="Más" theme={theme} onClose={() => setMoreOpen(false)}>
            {showMensajes && (
              <SheetOption
                label="Mensajes"
                selected={activeView === "mensajes"}
                onClick={() => {
                  setMoreOpen(false);
                  setActiveView("mensajes");
                  window.scrollTo({ top: 0 });
                }}
              />
            )}
            {hasYoutube && (
              <SheetOption
                label="YouTube"
                selected={activeView === "youtube"}
                onClick={() => {
                  setMoreOpen(false);
                  setActiveView("youtube");
                  window.scrollTo({ top: 0 });
                }}
              />
            )}
            <div className="sbx-m-group">Cuenta</div>
            <a className="sbx-m-option" href="/auth/logout" style={{ textDecoration: "none" }}>
              Cerrar sesión
            </a>
          </MobileSheet>
        </>
      )}

      {!propRange && range.preset === "custom" && <SocialCustomDates range={range} onChange={changeRange} />}

      {!propRange && customIncomplete && (
        <div className="detail-notice" style={{ padding: "8px 12px", borderRadius: "8px", fontSize: "12px" }}>
          Elige las fechas del rango personalizado.
        </div>
      )}

      {error && (
        <div className="detail-notice is-danger" style={{ marginBottom: "16px", padding: "10px 14px", borderRadius: "8px" }}>
          {error}
        </div>
      )}

      {isDesktop ? (
        <SocialOrigenSelector
          theme={theme}
          origen={origen}
          counts={origenCounts}
          onChange={setOrigen}
          disabled={activeView === "mensajes"}
        />
      ) : (
        <SocialOrigenSelectorMobile
          theme={theme}
          origen={origen}
          counts={origenCounts}
          onChange={setOrigen}
          disabled={activeView === "mensajes"}
        />
      )}

      {origenAviso ? (
        <div className="sbx sbx-origen-empty" data-theme={theme} role="status" data-testid="origen-vacio">
          <p>{origenAviso}</p>
          <button type="button" className="sbx-link" onClick={() => setOrigen("all")}>
            Ver todo
          </button>
        </div>
      ) : activeView === "mensajes" && showMensajes ? (
        <SocialMensajesTab clientSlug={clientSlug} theme={theme} />
      ) : activeView === "youtube" && hasYoutube ? (
        <SocialYoutubeComentarios comments={youtubeComments} theme={theme} />
      ) : activeView === "temas" ? (
        <SandboxTemas
          key={temasFocusKey ?? "todos"}
          initialTopic={temasFocusKey}
          theme={theme}
          rangeComments={rangeComments}
          prevComments={prevComments}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          urgentCount={urgentCount}
          showHeader={effectiveShowHeader}
          onOpenBandeja={openBandeja}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          onNavigateToLocales={hasLocales ? () => openLocales() : undefined}
          onExportPdf={exportPdf}
        />
      ) : activeView === "locales" && hasLocales ? (
        <SandboxLocales
          key={localesFocusId ?? "comparativo"}
          initialLocalId={localesFocusId}
          onNavigateToTemas={() => openTemas()}
          theme={theme}
          rangeComments={rangeComments}
          allComments={scopedRecent}
          locales={googleLocales}
          prevComments={prevComments}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          urgentCount={urgentCount}
          showHeader={effectiveShowHeader}
          onOpenBandeja={openBandeja}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          onExportPdf={exportPdf}
        />
      ) : activeView === "resumen" ? (
        <SandboxResumen
          clientSlug={clientSlug}
          overview={overview}
          loading={loadingOverview || switching}
          rangeComments={rangeComments}
          allComments={scopedRecent}
          locales={googleLocales}
          rangeFrom={tableFrom}
          rangeTo={tableTo}
          platform={platform}
          availablePlatforms={availablePlatforms}
          onPlatformChange={setPlatform}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          theme={theme}
          prevComments={prevComments}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          onNavigateToBandeja={() => openBandeja()}
          onOpenComment={openComment}
          origen={origen}
          onNavigateToLocales={hasLocales ? openLocales : undefined}
          onNavigateToTemas={openTemas}
          showHeader={effectiveShowHeader}
          exportRef={exportRef}
        />
      ) : (
        <SandboxBandeja
          clientSlug={clientSlug}
          comments={rangeComments}
          allComments={scopedRecent}
          overview={overview}
          loading={loadingOverview}
          theme={theme}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          urgentCount={urgentCount}
          showHeader={effectiveShowHeader}
          focus={bandejaFocus ?? undefined}
          onNavigateToLocales={hasLocales ? () => openLocales() : undefined}
          onNavigateToTemas={openTemas}
        />
      )}
    </>
  );
}
